class FloatingAtc extends HTMLElement {
	static SELECTORS = {
		// Internal
		SUBSCRIBE_INPUT: '[data-floating-atc-subscribe]',
		SUBSCRIBE_TOGGLE: '[data-floating-atc-subscribe-toggle]',
		FREQUENCY_ROW: '[data-floating-atc-frequency]',
		PLAN_SELECT: '[data-floating-atc-plan-select]',
		QUANTITY: '[data-floating-atc-quantity]',
		ATC_BUTTON: '[data-floating-atc-button]',
		ATC_BUTTON_TEXT: '[data-floating-atc-button-text]',
		// External — REAL_* are resolved relative to PRODUCT_FORM_HOST in initializeElements.
		PRODUCT_FORM_HOST: 'product-form',
		REAL_FORM: 'form',
		REAL_ATC_BUTTON: '[data-test-id="product-atc-button"]',
		REAL_ATC_BUTTON_TEXT: '.button__text',
		REAL_QUANTITY_INPUT: '.quantity__input',
		PLAN_FREQUENCY: '#plan-picker-input-frequency',
		PLAN_SELLINGPLAN: '#plan-picker-input-sellingplan',
		PLAN_RECURRING_RADIO: '#plan-picker-input-recurring',
		PLAN_ONETIME_RADIO: '#plan-picker-input-onetime',
		PRODUCT_MANAGER: 'product-manager',
		// Card-style pages keep the top price in a template rather than showing it.
		TOP_PRICE: '.product__price, template[data-product-price-source]',
	};

	static CLASSES = {
		ACTIVE: 'floating-atc--active',
		// Mirrored on <body> so fixed elements elsewhere (the popup corner card) can clear the
		// bar without :has(), which is Safari 15.4 and above this theme's floor.
		BODY_ACTIVE: 'has-floating-atc',
	};

	static PURCHASE_TYPE_ATTRIBUTE = 'data-purchase-type';

	connectedCallback() {
		this.initializeElements();
		if (!this.elements.productForm) {
			return;
		}
		this.scrolledPast = false;
		this.footerVisible = false;
		this.syncFromReal();
		this.addEventListeners();
		this.initializeObserver();
		this.initializeFooterObserver();
		this.observeRealAtc();
		this.observePurchaseType();
	}

	disconnectedCallback() {
		// Theme-editor section reloads detach + reattach; without teardown, subs/observers stack.
		this.productEventUnsubscriber?.();
		this.foldObserver?.disconnect();
		this.footerObserver?.disconnect();
		this.realAtcObserver?.disconnect();
		this.purchaseTypeObserver?.disconnect();
		if (this.boundOnFocusOut) {
			this.removeEventListener('focusout', this.boundOnFocusOut);
		}
		document.body.classList.remove(FloatingAtc.CLASSES.BODY_ACTIVE);
	}

	initializeElements() {
		// Bind to the primary <product-form>; scoping real-form lookups under it avoids cross-talk with future bundle/cross-sell forms on the same template.
		const productFormHost = document.querySelector(FloatingAtc.SELECTORS.PRODUCT_FORM_HOST);
		this.formId = productFormHost?.dataset.formId || null;

		this.elements = {
			subscribeInput: this.querySelector(FloatingAtc.SELECTORS.SUBSCRIBE_INPUT),
			subscribeToggle: this.querySelector(FloatingAtc.SELECTORS.SUBSCRIBE_TOGGLE),
			frequencyRow: this.querySelector(FloatingAtc.SELECTORS.FREQUENCY_ROW),
			planSelect: this.querySelector(FloatingAtc.SELECTORS.PLAN_SELECT),
			quantity: this.querySelector(FloatingAtc.SELECTORS.QUANTITY),
			atcButton: this.querySelector(FloatingAtc.SELECTORS.ATC_BUTTON),
			atcButtonText: this.querySelector(FloatingAtc.SELECTORS.ATC_BUTTON_TEXT),
			productFormHost,
			productForm: productFormHost?.querySelector(FloatingAtc.SELECTORS.REAL_FORM),
			// foldTarget stays the persistent <product-form> host (survives variant-change rerenders).
			foldTarget: productFormHost,
			// Resolved (and re-resolved on rerender) by resolveRealRefs.
			realAtcButton: null,
			realQuantityInput: null,
			planFrequency: null,
			planSellingPlan: null,
			recurringRadio: null,
			onetimeRadio: null,
		};
		this.resolveRealRefs();
	}

	// (Re)resolve elements that live in rerenderable subtrees. The buy-row's <buy-buttons> renders inside <product-form> (default) or the plan-picker card (card style), so find it by form id, not DOM containment.
	resolveRealRefs() {
		const buyButtons =
			(this.formId && document.querySelector(`buy-buttons[data-form-id="${this.formId}"]`)) ||
			this.elements.productFormHost?.querySelector('buy-buttons') ||
			null;
		this.elements.realAtcButton = buyButtons?.querySelector(FloatingAtc.SELECTORS.REAL_ATC_BUTTON) || null;
		this.elements.realQuantityInput = buyButtons?.querySelector(FloatingAtc.SELECTORS.REAL_QUANTITY_INPUT) || null;
		this.elements.planFrequency = document.querySelector(FloatingAtc.SELECTORS.PLAN_FREQUENCY);
		this.elements.planSellingPlan = document.querySelector(FloatingAtc.SELECTORS.PLAN_SELLINGPLAN);
		this.elements.recurringRadio = document.querySelector(FloatingAtc.SELECTORS.PLAN_RECURRING_RADIO);
		this.elements.onetimeRadio = document.querySelector(FloatingAtc.SELECTORS.PLAN_ONETIME_RADIO);
	}

	addEventListeners() {
		// Outbound: floating UI → real form
		this.elements.subscribeInput?.addEventListener('change', this.onSubscribeChange.bind(this));
		this.elements.planSelect?.addEventListener('change', this.onPlanSelectChange.bind(this));
		this.elements.quantity?.addEventListener('change', this.onQuantityChange.bind(this));
		this.elements.atcButton?.addEventListener('click', this.onAtcClick.bind(this));

		this.bindInboundListeners();

		// Mirror price re-renders from product-manager; capture unsubscriber for disconnectedCallback.
		if (window.UFT?.EventManager) {
			this.productEventUnsubscriber = window.UFT.EventManager.on(
				window.UFT.EventManager.eventStrings.PRODUCT_HTML_UPDATED,
				this.onProductHtmlUpdated.bind(this)
			);
		}
	}

	// Inbound: real form → floating UI. Real targets are replaced by variant-change rerenders, so this is re-run after each rerender; stable bound refs keep addEventListener idempotent on unchanged nodes.
	bindInboundListeners() {
		this.boundSyncQuantityFromReal ||= this.syncQuantityFromReal.bind(this);
		this.boundSyncSubscribeFromReal ||= this.syncSubscribeFromReal.bind(this);
		this.boundSyncFrequencyFromReal ||= this.syncFrequencyFromReal.bind(this);
		this.elements.realQuantityInput?.addEventListener('change', this.boundSyncQuantityFromReal);
		this.elements.recurringRadio?.addEventListener('change', this.boundSyncSubscribeFromReal);
		this.elements.onetimeRadio?.addEventListener('change', this.boundSyncSubscribeFromReal);
		this.elements.planFrequency?.addEventListener('change', this.boundSyncFrequencyFromReal);
	}

	initializeObserver() {
		if (!this.elements.foldTarget) {
			return;
		}
		this.foldObserver = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					// Active when the real buy-row has scrolled above the viewport top.
					this.scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
					this.updateActive();
				});
			},
			{ rootMargin: '0px', threshold: 0 }
		);
		this.foldObserver.observe(this.elements.foldTarget);
		this.boundOnFocusOut ||= (event) => this.updateActive(event.relatedTarget);
		this.addEventListener('focusout', this.boundOnFocusOut);
	}

	// The bar is position:fixed, so it would sit on top of the footer at the end of the
	// page. Slide it away while any part of the footer is on screen instead of reserving
	// space with padding.
	initializeFooterObserver() {
		// body > footer: the footer-group sections render as direct body children with
		// tag "footer". A bare 'footer' would match in-content footers (e.g. review cards).
		const footer = document.querySelector('body > footer');
		if (!footer) {
			return;
		}
		this.footerObserver = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					this.footerVisible = entry.isIntersecting;
					this.updateActive();
				});
			},
			{ rootMargin: '0px', threshold: 0 }
		);
		this.footerObserver.observe(footer);
	}

	// nextFocus: during focusout browsers report document.activeElement as <body>, so the
	// listener passes event.relatedTarget instead. Observers call this with no argument.
	updateActive(nextFocus = document.activeElement) {
		// Never slide away while the bar holds focus — the shopper would lose their place in the
		// tab order mid-interaction (WCAG 2.4.3). It hides on the next update once focus leaves.
		if (nextFocus && this.contains(nextFocus)) {
			return;
		}
		const active = this.scrolledPast && !this.footerVisible;
		this.classList.toggle(FloatingAtc.CLASSES.ACTIVE, active);
		document.body.classList.toggle(FloatingAtc.CLASSES.BODY_ACTIVE, active);
	}

	// ---------- Sync (real form → floating UI) ----------

	syncFromReal() {
		this.syncQuantityFromReal();
		this.syncSubscribeAvailabilityFromReal();
		this.syncSubscribeFromReal();
		this.syncFrequencyFromReal();
		this.syncAtcFromReal();
	}

	syncQuantityFromReal() {
		if (!this.elements.quantity || !this.elements.realQuantityInput) {
			return;
		}
		this.syncQuantityOptionsFromReal();
		const real = this.elements.realQuantityInput;
		const min = parseInt(real.min || '1', 10) || 1;
		const rawMax = parseInt(real.max, 10);
		const max = Math.min(99, Number.isNaN(rawMax) ? 99 : rawMax);
		const value = parseInt(real.value, 10) || min;
		this.elements.quantity.value = String(Math.min(Math.max(value, min), max));
	}

	// Trim 1–99 options to realInput.min/max so the shopper can't pick a value the cart would reject server-side.
	syncQuantityOptionsFromReal() {
		if (!this.elements.quantity || !this.elements.realQuantityInput) {
			return;
		}
		const real = this.elements.realQuantityInput;
		const min = parseInt(real.min || '1', 10) || 1;
		const rawMax = parseInt(real.max, 10);
		const max = Math.min(99, Number.isNaN(rawMax) ? 99 : rawMax);
		Array.from(this.elements.quantity.options).forEach((opt) => {
			const optVal = parseInt(opt.value, 10);
			const outOfRange = optVal < min || optVal > max;
			opt.hidden = outOfRange;
			opt.disabled = outOfRange;
		});
	}

	// This section renders once and never re-renders, so whether the current variant offers Subscribe & Save comes
	// from the live plan picker. Without it, a sale or plan-less variant kept a toggle that flipped on and did
	// nothing, while Add to Cart still added a one-time line.
	syncSubscribeAvailabilityFromReal() {
		if (!this.elements.subscribeToggle) {
			return;
		}
		this.elements.subscribeToggle.hidden = !this.elements.recurringRadio;
	}

	syncSubscribeFromReal() {
		if (!this.elements.subscribeInput) {
			return;
		}
		const subscribing = !!this.elements.recurringRadio?.checked;
		this.elements.subscribeInput.checked = subscribing;
		this.toggleFrequencyRow(subscribing);
	}

	syncFrequencyFromReal() {
		if (!this.elements.planSelect || !this.elements.planFrequency) {
			return;
		}
		this.syncFrequencyOptionsFromReal();
		this.elements.planSelect.value = this.elements.planFrequency.value;
	}

	// The server rendered the frequencies of the landing variant, or else the first variant with a storefront plan,
	// which can differ from the variant on screen now, so mirror the live picker's options (and optgroups) whenever
	// its plan ids differ.
	syncFrequencyOptionsFromReal() {
		const realPlanIds = Array.from(this.elements.planFrequency.options, (option) => option.value).join();
		const floatingPlanIds = Array.from(this.elements.planSelect.options, (option) => option.value).join();
		if (realPlanIds === floatingPlanIds) {
			return;
		}
		const mirroredOptions = Array.from(this.elements.planFrequency.children, (node) => node.cloneNode(true));
		this.elements.planSelect.replaceChildren(...mirroredOptions);
	}

	// Source label from `.button__text` — realAtc.textContent leaks the SVG icon's <title>.
	syncAtcFromReal() {
		if (!this.elements.atcButton || !this.elements.realAtcButton) {
			return;
		}
		this.elements.atcButton.disabled = this.elements.realAtcButton.disabled;
		const labelNode = this.elements.realAtcButton.querySelector(FloatingAtc.SELECTORS.REAL_ATC_BUTTON_TEXT);
		const realLabel = ((labelNode || this.elements.realAtcButton).textContent || '').trim();
		if (!realLabel || !this.elements.atcButtonText) {
			return;
		}
		if (this.elements.atcButtonText.textContent !== realLabel) {
			this.elements.atcButtonText.textContent = realLabel;
		}
	}

	// Catch variant flips to sold-out, mid-add `disabled` toggles, and in-place `.button__text` edits — the initial sync would otherwise go stale.
	observeRealAtc() {
		this.realAtcObserver?.disconnect();
		if (!this.elements.realAtcButton || typeof MutationObserver !== 'function') {
			return;
		}
		this.realAtcObserver = new MutationObserver(() => {
			this.syncAtcFromReal();
		});
		this.realAtcObserver.observe(this.elements.realAtcButton, {
			attributeFilter: ['disabled'],
			characterData: true,
			childList: true,
			subtree: true,
		});
	}

	// The price and Sezzle hold both purchase types, and theme.css shows the one matching data-purchase-type. This
	// section sits outside product-manager, so it mirrors the attribute. The observer's callback runs after
	// ProductManager finishes writing it and before the browser paints.
	observePurchaseType() {
		this.purchaseTypeObserver?.disconnect();
		const productManager = document.querySelector(FloatingAtc.SELECTORS.PRODUCT_MANAGER);
		if (!productManager) {
			return;
		}
		const attribute = FloatingAtc.PURCHASE_TYPE_ATTRIBUTE;
		const mirror = () => this.setAttribute(attribute, productManager.getAttribute(attribute) ?? '');
		mirror();
		this.purchaseTypeObserver = new MutationObserver(mirror);
		this.purchaseTypeObserver.observe(productManager, { attributeFilter: [attribute] });
	}

	toggleFrequencyRow(visible) {
		if (!this.elements.frequencyRow) {
			return;
		}
		this.elements.frequencyRow.hidden = !visible;
	}

	// ---------- Outbound (floating UI → real form) ----------

	onSubscribeChange(event) {
		const subscribing = event.target.checked;
		const target = subscribing ? this.elements.recurringRadio : this.elements.onetimeRadio;
		if (!target) {
			return;
		}
		target.checked = true;
		// Plan-picker listens for 'change' to drive accordion + selling_plan state.
		target.dispatchEvent(new Event('change', { bubbles: true }));
		this.toggleFrequencyRow(subscribing);
	}

	onPlanSelectChange(event) {
		if (!this.elements.planFrequency) {
			return;
		}
		this.elements.planFrequency.value = event.target.value;
		this.elements.planFrequency.dispatchEvent(new Event('change', { bubbles: true }));
	}

	onQuantityChange(event) {
		if (!this.elements.realQuantityInput) {
			return;
		}
		this.elements.realQuantityInput.value = event.target.value;
		// product-form.js / cart-add listen on the real input's 'change'.
		this.elements.realQuantityInput.dispatchEvent(new Event('change', { bubbles: true }));
	}

	onAtcClick() {
		// Click the real button so its listeners (analytics, BIS hooks, disabled guard) fire.
		if (this.elements.realAtcButton && !this.elements.realAtcButton.disabled) {
			this.elements.realAtcButton.click();
		}
	}

	onProductHtmlUpdated({ section }) {
		if (!section) {
			return;
		}
		// Listeners run in subscription order, which follows section order and changes on theme-editor reloads, so
		// wait until this dispatch finishes and the plan picker has swapped in the new variant's markup.
		queueMicrotask(() => this.syncFromRerender(section));
	}

	syncFromRerender(section) {
		// Variant change replaces the buy-row and plan-picker subtrees (the whole card, in card style), so re-resolve the real refs and re-wire — otherwise the ATC click, observer, and sync hold stale detached nodes.
		this.resolveRealRefs();
		this.bindInboundListeners();
		this.observeRealAtc();
		this.syncFromReal();

		const floatingPrice = this.querySelector('[data-floating-atc-price]');
		if (!floatingPrice) {
			return;
		}
		const sourcePrice = this.resolvePriceSource(section);
		if (sourcePrice) {
			floatingPrice.innerHTML = sourcePrice.innerHTML;
		}
	}

	// The top price holds both purchase types, so the mirrored data-purchase-type keeps choosing between them. A
	// template without the price block falls back to the selected plan-picker option's price.
	resolvePriceSource(section) {
		const topPrice = section.querySelector(FloatingAtc.SELECTORS.TOP_PRICE);
		if (topPrice) {
			return topPrice;
		}
		// Read the live radio, not the cached ref — a variant-change rerender detaches it.
		const liveRecurring = document.querySelector(FloatingAtc.SELECTORS.PLAN_RECURRING_RADIO);
		const subscribing = !!liveRecurring?.checked;
		const optionId = subscribing
			? FloatingAtc.SELECTORS.PLAN_RECURRING_RADIO
			: FloatingAtc.SELECTORS.PLAN_ONETIME_RADIO;
		const optionRadio = section.querySelector(optionId);
		const optionContainer = optionRadio?.closest('.plan-picker__radio');
		return optionContainer?.querySelector('.plan-picker__option-price') || null;
	}
}

customElements.define('floating-atc', FloatingAtc);
